import { FC, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { TECHNO_COLORS } from "../../theme";

const COLORS = Object.values(TECHNO_COLORS).concat("#FFFFFF");
const BURST_COUNT = 14;
const TRAIL_EVERY_MS = 35;
// Hard cap so a frantic mouse over a big grid cannot pile up DOM nodes.
const MAX_LIVE = 120;

export const technoSparkleStyles = {
  "@keyframes techno-sparkle": {
    "0%": {
      opacity: 1,
      transform: "translate(-50%, -50%) scale(0.4) rotate(0deg)"
    },
    "30%": { opacity: 1 },
    "100%": {
      opacity: 0,
      transform:
        "translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1.1) rotate(var(--spin))"
    }
  },
  // Used by the techno logo's resting sparkles.
  "@keyframes techno-twinkle": {
    "0%, 100%": { opacity: 0, transform: "scale(0.3) rotate(0deg)" },
    "50%": { opacity: 1, transform: "scale(1) rotate(90deg)" }
  },
  ".techno-sparkle": {
    position: "absolute",
    lineHeight: 1,
    pointerEvents: "none",
    willChange: "transform, opacity",
    animation: "techno-sparkle var(--life) ease-out forwards"
  }
};

const pick = <T,>(list: T[]) => list[Math.floor(Math.random() * list.length)];

/**
 * Sparkles for techno mode: a burst wherever you click, and a trail while the pointer moves
 * over data grid rows. Plain DOM nodes in one fixed layer rather than React state, since they
 * live for under a second and there can be dozens at once. Skipped entirely under
 * prefers-reduced-motion.
 */
export const Sparkles: FC = () => {
  const layer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const spawn = (x: number, y: number, distance: number, life: number) => {
      const root = layer.current;
      if (!root || root.childElementCount >= MAX_LIVE) return;
      const color = pick(COLORS);
      const angle = Math.random() * Math.PI * 2;
      const reach = distance * (0.5 + Math.random() * 0.5);
      const el = document.createElement("span");
      el.className = "techno-sparkle";
      el.textContent = Math.random() < 0.7 ? "✦" : "✧";
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.fontSize = `${8 + Math.random() * 12}px`;
      el.style.color = color;
      el.style.textShadow = `0 0 6px ${color}, 0 0 12px ${color}`;
      el.style.setProperty("--dx", `${Math.cos(angle) * reach}px`);
      el.style.setProperty("--dy", `${Math.sin(angle) * reach}px`);
      el.style.setProperty("--spin", `${(Math.random() - 0.5) * 360}deg`);
      el.style.setProperty("--life", `${life}ms`);
      el.addEventListener("animationend", () => el.remove());
      root.appendChild(el);
    };

    const onPointerDown = (e: PointerEvent) => {
      for (let i = 0; i < BURST_COUNT; i++) {
        spawn(e.clientX, e.clientY, 70, 650 + Math.random() * 300);
      }
    };

    let lastTrail = 0;
    const onPointerMove = (e: PointerEvent) => {
      if (e.timeStamp - lastTrail < TRAIL_EVERY_MS) return;
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest(".MuiDataGrid-row")) return;
      lastTrail = e.timeStamp;
      spawn(e.clientX, e.clientY, 28, 500 + Math.random() * 250);
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
    };
  }, []);

  return (
    <Box
      ref={layer}
      aria-hidden
      sx={{
        position: "fixed",
        inset: 0,
        // Above dialogs too, so a click inside one still sparkles; it never takes pointer events.
        zIndex: 1400,
        pointerEvents: "none",
        overflow: "hidden"
      }}
    />
  );
};
